/**
 * Prefetch Service - Preloads trends and sounds data after login
 * This reduces perceived loading time by fetching data in the background
 * OPTIMIZED: Uses deferred loading to not block dashboard
 */

import { trendsAPI, soundsAPI } from './api';

// Cache storage with timestamps
const cache = {
  trends: { data: null, timestamp: null, loading: false, promise: null },
  sounds: { data: null, timestamp: null, loading: false, promise: null },
};

// Cache duration in milliseconds (5 minutes for client-side cache)
const CACHE_DURATION = 5 * 60 * 1000;

// Delay before starting prefetch (ms) - allows dashboard to load first
const PREFETCH_DELAY = 1500;

// Check if cache is valid
const isCacheValid = (cacheEntry) => {
  if (!cacheEntry.data || !cacheEntry.timestamp) return false;
  return Date.now() - cacheEntry.timestamp < CACHE_DURATION;
};

/**
 * Prefetch all data after login
 * Call this immediately after successful authentication
 * Uses setTimeout to defer execution and not block UI
 */
export const prefetchAllData = async () => {
  console.log('[Prefetch] Scheduling background data prefetch...');
  
  // Defer prefetch to allow dashboard to load first
  setTimeout(() => {
    console.log('[Prefetch] Starting deferred prefetch...');
    
    // Fetch both in parallel but don't await - true background loading
    prefetchTrends().catch(err => console.warn('[Prefetch] Trends failed:', err));
    prefetchSounds().catch(err => console.warn('[Prefetch] Sounds failed:', err));
  }, PREFETCH_DELAY);
  
  // Return immediately - don't block
  return Promise.resolve();
};

/**
 * Prefetch trends data
 * Returns existing promise if already loading (prevents duplicate requests)
 */
export const prefetchTrends = async (forceRefresh = false) => {
  // Return cached data if valid and not forcing refresh
  if (!forceRefresh && isCacheValid(cache.trends)) {
    console.log('[Prefetch] Using cached trends data');
    return cache.trends.data;
  }
  
  // Return existing promise if already loading (deduplication)
  if (cache.trends.loading && cache.trends.promise) {
    console.log('[Prefetch] Trends already loading, returning existing promise');
    return cache.trends.promise;
  }
  
  // Create new promise for this request
  cache.trends.loading = true;
  cache.trends.promise = (async () => {
    try {
      console.log('[Prefetch] Fetching trends data...');
      const response = await trendsAPI.getTrends(forceRefresh);
      cache.trends.data = response.data;
      cache.trends.timestamp = Date.now();
      console.log(`[Prefetch] Trends cached: ${response.data?.length || 0} items`);
      return response.data;
    } catch (error) {
      console.error('[Prefetch] Failed to prefetch trends:', error);
      return null;
    } finally {
      cache.trends.loading = false;
      cache.trends.promise = null;
    }
  })();
  
  return cache.trends.promise;
};

/**
 * Prefetch sounds data
 * Returns existing promise if already loading (prevents duplicate requests)
 */
export const prefetchSounds = async () => {
  // Return cached data if valid
  if (isCacheValid(cache.sounds)) {
    console.log('[Prefetch] Using cached sounds data');
    return cache.sounds.data;
  }
  
  // Return existing promise if already loading (deduplication)
  if (cache.sounds.loading && cache.sounds.promise) {
    console.log('[Prefetch] Sounds already loading, returning existing promise');
    return cache.sounds.promise;
  }
  
  // Create new promise for this request
  cache.sounds.loading = true;
  cache.sounds.promise = (async () => {
    try {
      console.log('[Prefetch] Fetching sounds data...');
      const response = await soundsAPI.getTrendingSounds();
      cache.sounds.data = response.data;
      cache.sounds.timestamp = Date.now();
      console.log(`[Prefetch] Sounds cached: ${response.data?.length || 0} items`);
      return response.data;
    } catch (error) {
      console.error('[Prefetch] Failed to prefetch sounds:', error);
      return null;
    } finally {
      cache.sounds.loading = false;
      cache.sounds.promise = null;
    }
  })();
  
  return cache.sounds.promise;
};

/**
 * Get cached trends (returns immediately with cached data or null)
 */
export const getCachedTrends = () => {
  if (isCacheValid(cache.trends)) {
    return cache.trends.data;
  }
  return null;
};

/**
 * Get cached sounds (returns immediately with cached data or null)
 */
export const getCachedSounds = () => {
  if (isCacheValid(cache.sounds)) {
    return cache.sounds.data;
  }
  return null;
};

/**
 * Clear all cached data (call on logout)
 */
export const clearPrefetchCache = () => {
  cache.trends = { data: null, timestamp: null, loading: false };
  cache.sounds = { data: null, timestamp: null, loading: false };
  console.log('[Prefetch] Cache cleared');
};

/**
 * Get trends with smart loading strategy:
 * 1. Return cached data immediately if available
 * 2. Fetch fresh data in background
 * 3. Return fresh data when ready
 */
export const getSmartTrends = async (forceRefresh = false) => {
  const cached = getCachedTrends();
  
  if (cached && !forceRefresh) {
    // Return cached immediately, refresh in background
    prefetchTrends(false).catch(() => {}); // Silent background refresh
    return { data: cached, fromCache: true };
  }
  
  // No cache, fetch fresh
  const fresh = await prefetchTrends(forceRefresh);
  return { data: fresh, fromCache: false };
};

/**
 * Get sounds with smart loading strategy
 */
export const getSmartSounds = async () => {
  const cached = getCachedSounds();
  
  if (cached) {
    // Return cached immediately, refresh in background
    prefetchSounds().catch(() => {}); // Silent background refresh
    return { data: cached, fromCache: true };
  }
  
  // No cache, fetch fresh
  const fresh = await prefetchSounds();
  return { data: fresh, fromCache: false };
};
